import React, { useEffect, useMemo, useState } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';

// ── helpers ──────────────────────────────────────────────────────────────────

// Mirrors the same dedup used in AttendanceTracking.jsx:
// collapse duplicate schedule rows that represent the same logical class slot,
// preferring the entry where attendance is already marked.
function deduplicateSchedules(list) {
  if (!Array.isArray(list) || list.length === 0) return [];

  const normalize = (value) => String(value ?? '').trim().toLowerCase();

  const map = new Map();

  for (const s of list) {
    const batchGroupName =
      s.batchGroupName ||
      s.batchGroupWhatsappName ||
      s.whatsappGroupName ||
      s.batchInternalName ||
      '';

    const key = [
      normalize(batchGroupName),
      normalize(s.subjectShortName || s.subjectFullName),
      normalize(s.startTime),
      normalize(s.endTime),
      normalize(s.staffName),
      normalize(s.classroomName),
    ].join('|');

    const existing = map.get(key);
    if (!existing) {
      map.set(key, s);
      continue;
    }

    const existingMarked =
      existing.isAttendanceMarked === true || existing.isAttendanceMarked === 'true';
    const currentMarked =
      s.isAttendanceMarked === true || s.isAttendanceMarked === 'true';

    // Prefer the marked entry to match AttendanceTracking behaviour
    if (!existingMarked && currentMarked) {
      map.set(key, s);
    }
  }

  return Array.from(map.values());
}

function fmt12h(t) {
  if (!t) return '—';
  const [h, m] = t.split(':');
  let hours = parseInt(h, 10);
  const mod = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${hours}:${m} ${mod}`;
}

function todayISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function dateLabel(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return new Date(Number(y), Number(m) - 1, Number(d)).toLocaleDateString('en-GB', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

// Matches the same logic used in AttendanceTracking.jsx
function normalizeLoose(str) {
  return String(str || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

function isHndComputing(v) {
  const n = normalizeLoose(v);
  return n.includes('hnd in computing') || (n.includes('hnd') && n.includes('comput'));
}

function getCourseLabel(s) {
  const name = (s.courseName || '').toString();
  const short = (s.courseShortName || '').toString();
  if (isHndComputing(name) || isHndComputing(short)) return 'HND in Computing';
  return (short || name || 'Unknown Course').toString().trim();
}

function getBatchDisplay(s) {
  return (
    s.batchGroupWhatsappName ||
    s.batchGroupName ||
    s.whatsappGroupName ||
    s.batchInternalName ||
    '—'
  );
}

function groupByCourse(schedules) {
  const map = {};
  schedules.forEach((s) => {
    const key = getCourseLabel(s);
    if (!map[key]) map[key] = [];
    map[key].push(s);
  });
  return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
}

function buildWhatsAppMessage(schedules, courseFilter, selectedDate) {
  const dateStr = dateLabel(selectedDate);
  const list = courseFilter
    ? schedules.filter((s) => getCourseLabel(s) === courseFilter)
    : schedules;

  const lines = [];
  lines.push('*⚠️ Attendance Not Marked — Action Required*');
  lines.push(`_${dateStr}_`);
  lines.push('');
  lines.push('Dear Coordinator,');
  lines.push('');
  lines.push(
    'Please be advised that attendance has *not been recorded* for the following class session(s) that have already concluded. Kindly ensure the attendance is updated at the earliest.'
  );
  lines.push('');

  if (list.length === 0) {
    lines.push('No pending sessions.');
  } else {
    const grouped = groupByCourse(list);
    grouped.forEach(([course, sessions]) => {
      lines.push(`*📚 ${course}*`);
      sessions.forEach((s) => {
        const subject = s.subjectShortName || s.subjectFullName || 'Unknown Subject';
        const batch = getBatchDisplay(s);
        const time = `${fmt12h(s.startTime)} – ${fmt12h(s.endTime)}`;
        const lecturer = s.staffName || '—';
        const room = s.classroomName ? ` | Room: ${s.classroomName}` : '';
        lines.push(`  • *${subject}* — ${batch}`);
        lines.push(`    🕐 ${time} | 👤 ${lecturer}${room}`);
      });
      lines.push('');
    });

    lines.push(`*Total sessions pending: ${list.length}*`);
    lines.push('');
    lines.push('Please update the attendance records immediately or inform us if there is a valid reason for the delay.');
    lines.push('');
    lines.push('Thank you.');
  }

  return lines.join('\n');
}

// ── CopyButton ────────────────────────────────────────────────────────────────

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.cssText = 'position:fixed;opacity:0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-100 transition-colors"
    >
      {copied ? (
        <>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          Copied!
        </>
      ) : (
        <>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
          </svg>
          Copy as WhatsApp Message
        </>
      )}
    </button>
  );
}

// ── Schedule row ──────────────────────────────────────────────────────────────

function ScheduleRow({ s }) {
  return (
    <tr className="border-b border-gray-100 hover:bg-amber-50 transition-colors">
      <td className="py-3 px-4">
        <p className="text-sm font-medium text-gray-900">{s.subjectFullName || s.subjectShortName || '—'}</p>
        {s.subjectShortName && s.subjectFullName && s.subjectShortName !== s.subjectFullName && (
          <p className="text-xs text-gray-400">{s.subjectShortName}</p>
        )}
      </td>
      <td className="py-3 px-4 text-sm text-gray-700">{getBatchDisplay(s)}</td>
      <td className="py-3 px-4">
        <div className="flex items-center gap-1 text-sm text-gray-700">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400 flex-shrink-0">
            <circle cx="12" cy="12" r="10" /><path strokeLinecap="round" d="M12 6v6l4 2" />
          </svg>
          {fmt12h(s.startTime)} – {fmt12h(s.endTime)}
        </div>
      </td>
      <td className="py-3 px-4 text-sm text-gray-700">{s.staffName || '—'}</td>
      <td className="py-3 px-4 text-sm text-gray-500">{s.classroomName || '—'}</td>
    </tr>
  );
}

// ── Course section ────────────────────────────────────────────────────────────

function CourseSection({ courseName, sessions }) {
  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-sm font-semibold text-gray-700">{courseName}</span>
        <span className="inline-flex items-center rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700 border border-red-200">
          {sessions.length} session{sessions.length !== 1 ? 's' : ''}
        </span>
      </div>
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full text-left">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="py-2 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Subject</th>
              <th className="py-2 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Batch / Group</th>
              <th className="py-2 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Time</th>
              <th className="py-2 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Lecturer</th>
              <th className="py-2 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Room</th>
            </tr>
          </thead>
          <tbody>
            {sessions.map((s) => (
              <ScheduleRow key={s.scheduleId} s={s} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function UnmarkedAttendanceNotifications() {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [courseFilter, setCourseFilter] = useState('');
  const [selectedDate, setSelectedDate] = useState(todayISO);

  useEffect(() => {
    if (!selectedDate) return;
    let cancelled = false;
    setLoading(true);
    setError('');

    axiosInstance
      .get('/admin/attendance/schedules', { params: { date: selectedDate } })
      .then((resp) => {
        if (cancelled) return;
        const raw = Array.isArray(resp?.data) ? resp.data : [];

        // Apply the same deduplication as AttendanceTracking so that a schedule
        // slot with any marked entry is not incorrectly shown as unmarked.
        const all = deduplicateSchedules(raw);

        const today = todayISO();
        const now = new Date();

        // For today: only sessions whose end time has already passed and attendance not marked
        // For past dates: all unmarked sessions
        const unmarked = all.filter((s) => {
          if (s.isAttendanceMarked) return false;
          if (selectedDate === today) {
            const [h, m] = (s.endTime || '').split(':');
            const endDate = new Date();
            endDate.setHours(Number(h) || 0, Number(m) || 0, 0, 0);
            return endDate <= now;
          }
          return true;
        });
        setSchedules(unmarked);
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load unmarked attendance data. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [selectedDate]);

  // Reset course filter when date changes
  useEffect(() => {
    setCourseFilter('');
  }, [selectedDate]);

  const allCourses = useMemo(() => {
    const set = new Set();
    schedules.forEach((s) => {
      const label = getCourseLabel(s);
      if (label) set.add(label);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [schedules]);

  const filtered = useMemo(
    () => (courseFilter ? schedules.filter((s) => getCourseLabel(s) === courseFilter) : schedules),
    [schedules, courseFilter]
  );

  const grouped = useMemo(() => groupByCourse(filtered), [filtered]);

  const whatsAppMessage = useMemo(
    () => buildWhatsAppMessage(schedules, courseFilter, selectedDate),
    [schedules, courseFilter, selectedDate]
  );

  const isToday = selectedDate === todayISO();

  return (
    <DashboardLayout>
      <div className="px-4 py-6 max-w-screen-xl mx-auto">
        {/* Page header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-1">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-100">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Unmarked Attendance</h1>
              <p className="text-sm text-gray-500">
                {dateLabel(selectedDate)}
                {isToday ? ' · Classes that ended without attendance being recorded' : ' · Sessions without attendance records'}
              </p>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3 flex-wrap">
            {/* Date Picker */}
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600 font-medium">Date:</label>
              <input
                type="date"
                value={selectedDate}
                max={todayISO()}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
              />
            </div>
            {/* Course filter */}
            <select
              value={courseFilter}
              onChange={(e) => setCourseFilter(e.target.value)}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
            >
              <option value="">All Courses ({schedules.length})</option>
              {allCourses.map((c) => (
                <option key={c} value={c}>
                  {c} ({schedules.filter((s) => getCourseLabel(s) === c).length})
                </option>
              ))}
            </select>
            {filtered.length > 0 && (
              <span className="inline-flex items-center rounded-full bg-red-50 border border-red-200 px-3 py-1 text-xs font-semibold text-red-700">
                {filtered.length} session{filtered.length !== 1 ? 's' : ''} pending
              </span>
            )}
          </div>
          <CopyButton text={whatsAppMessage} />
        </div>

        {/* Message preview */}
        <details className="mb-6">
          <summary className="cursor-pointer text-sm font-medium text-gray-600 hover:text-indigo-600 transition-colors select-none">
            Preview WhatsApp message
          </summary>
          <div className="mt-3 rounded-xl border border-gray-200 bg-gray-50 p-4">
            <pre className="whitespace-pre-wrap text-xs text-gray-700 font-sans leading-relaxed">
              {whatsAppMessage}
            </pre>
          </div>
        </details>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-24 text-gray-400">
            <svg className="animate-spin mr-3" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Loading unmarked sessions…
          </div>
        ) : error ? (
          <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-4 text-sm">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-gray-400">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="mb-3 text-green-400">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-base font-medium text-gray-600">All attendance up to date</p>
            <p className="text-sm mt-1">No {isToday ? 'ended ' : ''}sessions are missing attendance records.</p>
          </div>
        ) : (
          <section>
            <div className="flex items-center gap-2 mb-4">
              <span className="h-2 w-2 rounded-full bg-red-500" />
              <h2 className="text-base font-bold text-red-700">Sessions {isToday ? 'ended — ' : ''}attendance not marked</h2>
            </div>
            {grouped.map(([course, sessions]) => (
              <CourseSection key={course} courseName={course} sessions={sessions} />
            ))}
          </section>
        )}
      </div>
    </DashboardLayout>
  );
}
